import { InputField } from "@/components/input/InputField";
import { RowSkeleton } from "@/components/skeleton/RowSkeleton";
import { Combobox, ComboboxContent, ComboboxEmpty, ComboboxInput, ComboboxItem, ComboboxList } from "@/components/ui/combobox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UNITS_OPTIONS } from "@/constants/xactimate";

interface SelectFieldOptions {
  value: string;
  label: string;
}
import type { IMissingMapping, IXactimateToolRowData } from "@/interfaces/payloads/xactimate";
import { estimationActions } from "@/redux/actions/estimation.actions";
import { estimationSelector } from "@/redux/selectors/estimation.selector";
import { useDispatch, useSelector } from "@/redux/store";
import { capitalizeWords } from "@rebuild/shared";
import { Trash2, RotateCcw } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { getDefaultActivity } from "../../utils/xactimate";
import { XACTIMATE_CATEGORIES } from "@rebuild/shared";

const activityOptions: SelectFieldOptions[] = [
  { label: "- (rem)", value: "-" },
  { label: "+ (r)", value: "+" },
  { label: "& (r+r)", value: "&" },
  { label: "m (mat)", value: "m" },
  { label: "i (install)", value: "i" },
  { label: "r (d+r)", value: "r" },
];

// Only show actual unit options (SF, LF, EA, etc.), not room-based measurement acronyms (F, C, W, PC, PF, etc.)
export const unitOptions: SelectFieldOptions[] = UNITS_OPTIONS;

const categoryOptions: SelectFieldOptions[] = XACTIMATE_CATEGORIES.map((category) => ({ label: category, value: category }));

export const useMissingMappingTable = () => {
  const dispatch = useDispatch();

  const [editedMissingMapping, setEditedMissingMapping] = useState<IXactimateToolRowData[]>([]);

  const missingMappings: IMissingMapping[] | undefined = useSelector(estimationSelector.missingMappings)?.map((mapping, index) => {
    return {
      ...mapping,
      index,
    };
  });

  useEffect(() => {
    const defaultEdited: IXactimateToolRowData[] = missingMappings
      ? missingMappings?.map((mapping, index) => {
          return {
            id: "",
            index,
            discard: mapping.lineItem.toLowerCase().includes("free text identified"),
            quantity: mapping.unit,
            acceptedLineString: mapping.lineItem,
            selectedLineItemId: mapping.lineItemId,
            activity: getDefaultActivity(mapping.lineItem),
            lineNoSpaces: mapping.lineItem.replace(/\s/g, "").trim().toLowerCase(),
          };
        })
      : [];

    setEditedMissingMapping(defaultEdited);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dispatch(
      estimationActions.setEstimationState({
        fixedMapping: editedMissingMapping,
      }),
    );
  }, [dispatch, editedMissingMapping]);

  const onDiscard = useCallback(
    (index: number) => {
      const copyState = structuredClone(editedMissingMapping ?? []);

      copyState[index].discard = !copyState[index].discard;
      setEditedMissingMapping(copyState);
    },
    [editedMissingMapping],
  );

  const onFillField = useCallback(
    (key: keyof IXactimateToolRowData, value: never, index: number) => {
      const copyState = structuredClone(editedMissingMapping ?? []);

      copyState[index][key] = value;
      setEditedMissingMapping(copyState);
    },
    [editedMissingMapping],
  );

  // Render table item based on key
  const renderItem = useCallback(
    (key: string, row: IMissingMapping) => {
      const value = row[key as keyof IMissingMapping];

      const copyState = structuredClone(editedMissingMapping ?? []);
      const currentMissingMappingIndex = copyState.findIndex(
        (detail) => detail.selectedLineItemId === row.lineItemId && detail.index === row.index,
      );

      const discard = copyState[currentMissingMappingIndex]?.discard;

      switch (key) {
        case "remove":
          return (
            <div className="flex flex-row items-center justify-center">
              {discard ? (
                <RotateCcw className="h-5 w-5 text-green-500" onClick={() => onDiscard(row.index!)} />
              ) : (
                <Trash2 color="#E45F58" className="h-4 w-4" onClick={() => onDiscard(row.index!)} />
              )}
            </div>
          );

        case "cat": {
          const catValue = copyState[currentMissingMappingIndex]?.cat;
          return (
            <div className="relative w-full">
              <Combobox
                items={categoryOptions}
                itemToStringValue={(option) => option.label}
                value={categoryOptions.find((opt) => opt.value === catValue) ?? null}
                onValueChange={(item) => onFillField("cat", item?.value as never, row.index!)}
              >
                <ComboboxInput showClear={!!catValue} className="h-8" disabled={discard} />
                <ComboboxContent className="pointer-events-auto">
                  <ComboboxEmpty>No results found</ComboboxEmpty>
                  <ComboboxList className="max-h-60 overflow-y-auto">
                    {(option) => {
                      return (
                        <ComboboxItem key={option.value} value={option}>
                          {option.label}
                        </ComboboxItem>
                      );
                    }}
                  </ComboboxList>
                </ComboboxContent>
              </Combobox>
            </div>
          );
        }

        case "variable":
          return (
            <InputField
              name={"selector"}
              disable={discard}
              inputClassName="h-8 w-full"
              onChange={(event) => {
                onFillField("variable", event.target.value as never, row.index!);
              }}
            />
          );

        case "activity":
          return (
            <Select
              disabled={discard}
              defaultValue={getDefaultActivity(row.lineItem)}
              onValueChange={(value) => onFillField("activity", value as never, row.index!)}
            >
              <SelectTrigger className="h-8 w-full">
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                {activityOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          );

        case "unit": {
          const unitValue = copyState[currentMissingMappingIndex]?.quantity;
          return (
            <div className="relative w-full">
              <Combobox
                items={unitOptions}
                itemToStringValue={(option) => option.label}
                value={unitOptions.find((opt) => opt.value === unitValue) ?? null}
                onValueChange={(item) => onFillField("quantity", item?.value as never, row.index!)}
              >
                <ComboboxInput showClear={!!unitValue} className="h-8" disabled={discard} />
                <ComboboxContent className="pointer-events-auto">
                  <ComboboxEmpty>No results found</ComboboxEmpty>
                  <ComboboxList className="max-h-60 overflow-y-auto">
                    {(option) => {
                      return (
                        <ComboboxItem key={option.value} value={option}>
                          {option.label}
                        </ComboboxItem>
                      );
                    }}
                  </ComboboxList>
                </ComboboxContent>
              </Combobox>
            </div>
          );
        }

        case "lineItem":
          return <p className={`${discard ? "cursor-not-allowed line-through opacity-50" : ""}`}>{capitalizeWords(row[key])}</p>;

        default:
          return value !== null && value !== undefined ? String(value) : "N/A";
      }
    },
    [editedMissingMapping, onDiscard, onFillField],
  );

  const renderSkeleton = useCallback(() => {
    return <RowSkeleton />;
  }, []);

  const filteredMissingMappings = useMemo(() => {
    return missingMappings?.filter((mapping) => !mapping.lineItem.toLowerCase().includes("free text identified"));
  }, [missingMappings]);

  return {
    renderItem,
    renderSkeleton,
    missingMappings: filteredMissingMappings,
    filteredMissingMappings,
  };
};
